<template>
	<TheHeader></TheHeader>
	
	<div class="container-fluid">
		<div class="container">
			<form name="form">
				<div class="contain row">
					<TheSidebar></TheSidebar>
					
					<div class="page-container card col-md-8">
						<ThePageHeader></ThePageHeader>
						
						<TabView class="p-1">
							<TabPanel>
								<template #header>
									<i class="pi pi-fw pi-file"></i>
									<span>Info</span>
								</template>
								
								<TheTabViewLoading v-if="isPageLoading"></TheTabViewLoading>
					
								<div class="row" v-if="!isPageLoading">
									<div class="col-md-12 p-4">
										<div class="row mb-3">
											<div class="col-md-4">
												<label class="col-form-label-title">Name:</label>
											</div>
											
											<div class="col-md-8">
												<InputText class="p-inputtext" type="text" maxlength="50" placeholder="Name" v-model="employee.name" readonly />
											</div>
										</div>
										
										<div class="row mb-3">
											<div class="col-md-4">
												<label class="col-form-label-title">Email Address:</label>
											</div>
										
											<div class="col-md-8">
												<Button class="col-md-12 text-start p-button-link ps-2 pe-0" :label="employee.emailAddress">
													<a class="p-button-link" :href="'mailto:' + employee.emailAddress">{{employee.emailAddress}}</a>
												</Button>
											</div>
										</div>

										<div class="row mb-3">
											<div class="col-md-4">
												<label class="col-form-label-title">Phone Number:</label>
											</div>
										
											<div class="col-md-8">
												<InputText class="p-inputtext" type="text" maxlength="15" v-model="employee.mobilePhone" readonly />
											</div>
										</div>

										<div class="row mb-3" v-if="employee.employeeCompanies != null && employee.employeeCompanies.length != 0">
											<Divider />
										</div>

										<div class="row mb-3" v-if="employee.employeeCompanies != null && employee.employeeCompanies.length != 0">
											<DataTable :value="employee.employeeCompanies" responsiveLayout="scroll" selectionMode="single">
												<Column style="width: 100%;" field="company.name" header="Company"></Column>
														
												<template #empty>
													<div class="error-no-record mt-3" v-if="employee.employeeCompanies.length <= 0">
														<FontAwesomeIcon :icon="['fas', 'image']" size="2x" />
														<h6><span>No Record Found</span></h6>
													</div>
												</template>	
											</DataTable>
										</div>
									</div>
								</div>
							</TabPanel>

							<TabPanel>
								<template #header>
									<i class="pi pi-fw pi-lock"></i>
									<span>Password</span>
								</template>
								
								<TheTabViewLoading v-if="isPageLoading"></TheTabViewLoading>
					
								<div class="row" v-if="!isPageLoading">
									<div class="col-md-12 p-4 d-flex">
										<div class="col-md-8 pe-2">
											<div class="row mb-3">
												<div class="col-md-5 required">
													<label class="col-form-label-title">Current Password:</label>
												</div>
												
												<div class="col-md-7">
													<Password maxlength="50" placeholder="Current Password" v-model="password.currentPassword" :feedback="false" toggleMask />
												</div>
											</div>
											
											<div class="row mb-3">
												<div class="col-md-5 required">
													<label class="col-form-label-title">New Password:</label>
												</div>
												
												<div class="col-md-7">
													<Password :strongRegex="strongRegex" :class="{'p-invalid': (passwordNotMatch || v$.password.newPassword.strongRegexValidator.$invalid) && password.newPassword != ''}" toggleMask maxlength="50" placeholder="New Password" v-model="password.newPassword"></Password>
												</div>
											</div>
											
											<div class="row mb-3">
												<div class="col-md-5 required">
													<label class="col-form-label-title">Confirm Password:</label>
												</div>
												
												<div class="col-md-7">
													<Password :strongRegex="strongRegex" :class="{'p-invalid': (passwordNotMatch || v$.password.confirmPassword.strongRegexValidator.$invalid) && password.confirmPassword != ''}" toggleMask maxlength="50" placeholder="Confirm Password" v-model="password.confirmPassword"></Password>
												</div>
												
												<div class="col-md-5" v-if="passwordNotMatch"></div>
												
												<small class="col-md-7 p-error" v-if="passwordNotMatch">Passwords do not matched.</small>
											</div>
										</div>

										<div class="col-md-4 ps-2">
											<div class="col-md-12">
												<div class="row">
													<div class="col-md-12">
														<label class="col-form-password-policy-title">Password Policy:</label>
														<ul class="pl-2 ml-2 mt-0 col-form-password-policy">
															<li v-for="policies in passwordPolicy" :key="policies">{{ policies }}</li>
														</ul>
													</div>
												</div>
											</div>
										</div>
									</div>

									<div class="row mb-3">
										<div class="col-md-4"></div>
											
										<div class="d-flex col-md-8">
											<Button class="p-button-sm" :disabled="v$.password.$invalid || isLoading" @click="updatePassword" label="Save"/>
												
											<TheLoading v-if="isLoading"></TheLoading>
										</div>
									</div>
								</div>
							</TabPanel>
						</TabView>
					</div>
					
					<TheFooter></TheFooter>
				</div>
			</form>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from "vue";
	import store from "@/store";
	import employeeApi from "@/api/employee-api"
	import roleMixin from "@/mixin/role"
	import passwordUtilsMixin, { strongRegexValidator } from "@/mixin/utils/password-utils";
	
	import { Md5 } from "ts-md5";
	
	import TheHeader from "@/components/TheHeader.vue";
	import TheFooter from "@/components/TheFooter.vue";
	import TheSidebar from "@/components/TheSidebar.vue";
	import ThePageHeader from "@/components/ThePageHeader.vue";
	import TheTabViewLoading from "@/components/TheTabViewLoading.vue";
	import TheLoading from "@/components/TheLoading.vue";
	
	import useVuelidate from "@vuelidate/core"
	import { required, email } from "@vuelidate/validators"
	
	export default defineComponent({
		mixins: [roleMixin, passwordUtilsMixin],
		components: {
			TheHeader,
			TheFooter,
			TheSidebar,
			ThePageHeader,
			TheTabViewLoading,
			TheLoading,
		},
		data() {
			return {
				employee: store.getters.getSession.employee,
				password: {currentPassword: '', newPassword: '', confirmPassword: ''},
				isPageLoading: false,
				isLoading: false,
				passwordNotMatch: false
			}
		},
		mounted: function() {
			this.isPageLoading = true;
			employeeApi.employeeView(this.employee.identifier)
				.then(response => {
					this.employee = response.data.employee;
					this.isPageLoading = false;
				})
				.catch(error => {
					this.isPageLoading = false;
				})
		},
		methods: {
			updatePassword() {
				this.passwordNotMatch = false;
				if (this.password.newPassword == this.password.confirmPassword) {
					this.isLoading = true;
					employeeApi.employeeUpdatePassword({
							user: {
								username: this.employee.emailAddress, 
								password: Md5.hashStr(this.password.currentPassword),
							},
							newPassword: Md5.hashStr(this.password.newPassword)
						})
						.then((response: any) => {
							this.password = {currentPassword: '', newPassword: '', confirmPassword: ''};
							this.isLoading = false;
							this.$toast.add({severity: "success", summary: "Success", detail: response.message, life: 6000});
						})
						.catch(error => {
							this.isLoading = false;
							this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
						})
				}
				
				else {
					this.passwordNotMatch = true;
				}
			}
		},
		setup() {
			return { v$: useVuelidate() }
		},
		validations() {
			return {
				password: {
					currentPassword: {
						required
					},
					newPassword: {
						required, strongRegexValidator
					},
					confirmPassword: {
						required, strongRegexValidator
					}
				}
			}
		}
	});
</script>
